import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, useSpring, useMotionValue } from "framer-motion";
import htm from "htm";
import { resolveCanon, reveal } from "./route-mask.js";
import { formatInstant, TIMEZONE_CHANGED_EVENT, timezoneOf } from "./time.js";

export const html = htm.bind(React.createElement);
export const M = motion;
export const F = React.Fragment;

export const SPRING = { type:"spring", stiffness:520, damping:34, mass:.58 };
export const SOFT   = { type:"tween", duration:.2, ease:[.16,1,.3,1] };
/** Primary desk enter — enter-only (no exit wait · no veil). */
export const FADE   = { duration:.18, ease:[.16,1,.3,1] };
/** Stage swap · slightly longer than FADE for readable motion */
export const STAGE  = { duration:.26, ease:[.16,1,.3,1] };
/** Micro lift on controls */
export const LIFT   = { type:"spring", stiffness:460, damping:26, mass:.55 };

/* ── icons: one family, 1.6 stroke, 16-box ───────────────────────────────── */
const P = {
  search:  '<circle cx="7" cy="7" r="5"/><path d="M11 11l3.2 3.2"/>',
  bell:    '<path d="M4.4 6.6a3.6 3.6 0 0 1 7.2 0c0 3 1.4 4 1.4 4H3s1.4-1 1.4-4z"/><path d="M6.6 13a1.6 1.6 0 0 0 2.8 0"/>',
  plus:    '<path d="M8 3.5v9M3.5 8h9"/>',
  minus:   '<path d="M3.5 8h9"/>',
  compass: '<circle cx="8" cy="8" r="6"/><path d="M10.2 5.8 9 9 5.8 10.2 7 7z"/>',
  layers:  '<path d="M8 2.4 14 5.6 8 8.8 2 5.6z"/><path d="m2.6 8.6 5.4 2.9 5.4-2.9"/>',
  globe:   '<circle cx="8" cy="8" r="6"/><path d="M2 8h12"/><ellipse cx="8" cy="8" rx="2.6" ry="6"/>',
  route:   '<circle cx="4" cy="12" r="1.8"/><circle cx="12" cy="4" r="1.8"/><path d="M5.6 10.6C8 8 6 6.6 8.4 5.2"/>',
  radio:   '<circle cx="8" cy="8" r="1.6"/><path d="M4.8 4.8a4.5 4.5 0 0 0 0 6.4M11.2 11.2a4.5 4.5 0 0 0 0-6.4"/><path d="M2.6 2.6a7.6 7.6 0 0 0 0 10.8M13.4 13.4a7.6 7.6 0 0 0 0-10.8"/>',
  target:  '<circle cx="8" cy="8" r="5.2"/><circle cx="8" cy="8" r="1.4"/><path d="M8 .8v2M8 13.2v2M.8 8h2M13.2 8h2"/>',
  close:   '<path d="M4 4l8 8M12 4l-8 8"/>',
  check:   '<path d="M3.5 8.4 6.4 11.2 12.5 4.8"/>',
  shield:  '<path d="M8 2 13 4.2v4c0 2.8-2 5-5 6.2-3-1.2-5-3.4-5-6.2v-4z"/>',
  logout:  '<path d="M6 3H3.4v10H6"/><path d="M9 5.4 11.6 8 9 10.6M11.6 8H6"/>',
  key:     '<circle cx="5.4" cy="8" r="2.6"/><path d="M8 8h6M12 8v2.4"/>',
  book:    '<path d="M3 3.4h4.2A1.8 1.8 0 0 1 9 5.2v7.4a1.4 1.4 0 0 0-1.4-1.2H3z"/><path d="M13 3.4H8.8A1.8 1.8 0 0 0 7 5.2v7.4a1.4 1.4 0 0 1 1.4-1.2H13z"/>',
  camera:  '<path d="M2 5.5A1.5 1.5 0 0 1 3.5 4h5A1.5 1.5 0 0 1 10 5.5v5A1.5 1.5 0 0 1 8.5 12h-5A1.5 1.5 0 0 1 2 10.5z"/><path d="M10 7l4-2v6l-4-2z"/>',
  siren:   '<path d="M4 13V9a4 4 0 0 1 8 0v4z"/><path d="M8 2v1.5M2.5 5l1 .8M13.5 5l-1 .8M3 13h10"/>',
  contact: '<path d="M3 3h4v10H3zM9 3h4v10H9z"/>',
  gateway: '<circle cx="8" cy="11" r="2"/><path d="M5 8a4 4 0 0 1 6 0M3 5.6a7.5 7.5 0 0 1 10 0"/>',
  door:    '<path d="M3.6 2.4h8.8v11.2H3.6z"/><path d="M6 2.4v11.2"/><circle cx="9.9" cy="8" r=".85"/>',
  mast:    '<path d="M8 2v11.4"/><path d="M4.2 13.4 8 4.2l3.8 9.2"/><path d="M5.5 10h5"/><circle cx="8" cy="2.4" r="1.1"/>',
  sensor:  '<path d="M8 13.4V9.6"/><path d="M3.4 9.6h9.2l-1.7-5.1a1.4 1.4 0 0 0-1.3-1H6.4a1.4 1.4 0 0 0-1.3 1z"/><path d="M6.2 11.9h3.6"/>',
  lidar:   '<circle cx="8" cy="8" r="2.1"/><path d="M8 1.6v2M8 12.4v2M1.6 8h2M12.4 8h2"/><path d="M3.9 3.9 5.3 5.3M10.7 10.7l1.4 1.4M12.1 3.9l-1.4 1.4M5.3 10.7l-1.4 1.4"/>',
  user:    '<circle cx="8" cy="5.4" r="2.6"/><path d="M3 13.4a5 5 0 0 1 10 0"/>',
  lock:    '<rect x="3.4" y="7" width="9.2" height="6.6" rx="1.6"/><path d="M5.6 7V5.2a2.4 2.4 0 0 1 4.8 0V7"/>',
  mail:    '<rect x="2.2" y="3.6" width="11.6" height="8.8" rx="1.6"/><path d="m2.6 4.6 5.4 4 5.4-4"/>',
  phone:   '<path d="M5.6 2.6 7 5.2 5.7 6.6a7.4 7.4 0 0 0 3.7 3.7l1.4-1.3 2.6 1.4v2a1.2 1.2 0 0 1-1.3 1.2A11.4 11.4 0 0 1 2.4 4.7 1.2 1.2 0 0 1 3.6 3.4h2z"/>',
  eye:     '<path d="M1.4 8S4 3.8 8 3.8 14.6 8 14.6 8 12 12.2 8 12.2 1.4 8 1.4 8z"/><circle cx="8" cy="8" r="1.9"/>',
  eyeOff:  '<path d="M6.2 4.2A6.6 6.6 0 0 1 8 4c4 0 6.6 4 6.6 4a12 12 0 0 1-2.2 2.6M4.3 5.5A12.4 12.4 0 0 0 1.4 8S4 12 8 12a6.5 6.5 0 0 0 2.3-.4"/><path d="M2.4 2.4l11.2 11.2"/>',
  arrowR:  '<path d="M3 8h10M9.4 4.4 13 8l-3.6 3.6"/>',
  arrowL:  '<path d="M13 8H3M6.6 4.4 3 8l3.6 3.6"/>',
  refresh: '<path d="M13.4 7A5.5 5.5 0 0 0 3.6 4.6M2.6 9A5.5 5.5 0 0 0 12.4 11.4"/><path d="M13.6 3.4V7h-3.4M2.4 12.6V9h3.4"/>',
  spark:   '<path d="M8 1.6 9.5 6 14 7.5 9.5 9 8 13.4 6.5 9 2 7.5 6.5 6z"/>',
  brain:   '<path d="M6.4 2.6a2 2 0 0 0-2 2 2 2 0 0 0-1 3.5 2.1 2.1 0 0 0 1.3 3.5 2 2 0 0 0 3.3.9V3.9a2 2 0 0 0-1.6-1.3z"/><path d="M9.6 2.6a2 2 0 0 1 2 2 2 2 0 0 1 1 3.5 2.1 2.1 0 0 1-1.3 3.5 2 2 0 0 1-3.3.9"/>',
  gauge:   '<path d="M2.6 12a6 6 0 1 1 10.8 0"/><path d="M8 12 10.6 7"/><circle cx="8" cy="12" r="1"/>',
  alert:   '<path d="M8 2.6 14.4 13H1.6z"/><path d="M8 6.6v3M8 11.2v.1"/>',
  info:    '<circle cx="8" cy="8" r="6.2"/><path d="M8 7.4v3.4M8 5.3v.1"/>',
  ban:     '<circle cx="8" cy="8" r="6"/><path d="m3.8 3.8 8.4 8.4"/>',
  clock:   '<circle cx="8" cy="8" r="6"/><path d="M8 4.6V8l2.4 1.6"/>',
  slide:   '<path d="M2.6 5h10.8M2.6 11h10.8"/><circle cx="6" cy="5" r="1.7"/><circle cx="10.4" cy="11" r="1.7"/>',
  chip:    '<rect x="4.4" y="4.4" width="7.2" height="7.2" rx="1.4"/><path d="M6.4 2v2.4M9.6 2v2.4M6.4 11.6V14M9.6 11.6V14M2 6.4h2.4M2 9.6h2.4M11.6 6.4H14M11.6 9.6H14"/>',
  cloud:   '<path d="M4.6 12a3 3 0 0 1-.3-6 4.2 4.2 0 0 1 8 .8A2.6 2.6 0 0 1 12 12z"/>',
  card:    '<rect x="1.8" y="3.6" width="12.4" height="8.8" rx="1.6"/><path d="M1.8 6.6h12.4"/>',
  trash:   '<path d="M3 4.4h10M6.4 4.4V3a.8.8 0 0 1 .8-.8h1.6a.8.8 0 0 1 .8.8v1.4"/><path d="M4.4 4.4 5 13a.9.9 0 0 0 .9.8h4.2A.9.9 0 0 0 11 13l.6-8.6"/>',
  plug:    '<path d="M6 2v4M10 2v4"/><path d="M4 6h8v2.4a4 4 0 0 1-8 0z"/><path d="M8 12.4V14"/>',
  wifiOff: '<path d="M3 5.6a9 9 0 0 1 3.4-1.9M9.8 3.8A9 9 0 0 1 13 5.6M5.4 8.2a5.6 5.6 0 0 1 1.5-.8M9.2 7.5a5.6 5.6 0 0 1 1.4.8"/><circle cx="8" cy="11.4" r="1"/><path d="M2 2l12 12"/>',
  server:  '<rect x="2.2" y="2.6" width="11.6" height="4.4" rx="1.2"/><rect x="2.2" y="9" width="11.6" height="4.4" rx="1.2"/><path d="M4.6 4.8v.1M4.6 11.2v.1"/>',
  grid:    '<rect x="2.4" y="2.4" width="4.6" height="4.6" rx="1.2"/><rect x="9" y="2.4" width="4.6" height="4.6" rx="1.2"/><rect x="2.4" y="9" width="4.6" height="4.6" rx="1.2"/><rect x="9" y="9" width="4.6" height="4.6" rx="1.2"/>',
  map:     '<path d="M2.4 3.8 6 2.4l4 1.8 3.6-1.4v9.4L10 13.6l-4-1.8-3.6 1.4z"/><path d="M6 2.4v9.4M10 4.2v9.4"/>',
  sliders: '<path d="M3 4.6h10M3 11.4h10"/><circle cx="6.6" cy="4.6" r="1.6"/><circle cx="10" cy="11.4" r="1.6"/>',
  download:'<path d="M8 2.6v7.2M5.2 7.4 8 10.2l2.8-2.8"/><path d="M3 12.4h10"/>',
  play:    '<path d="M5.4 3.4 12 8l-6.6 4.6z"/>',
};
export const Ico = ({ d, w = 16, s }) => html`<svg viewBox="0 0 16 16" style=${{ width:w, height:w, ...(s||{}) }}
  strokeLinecap="round" strokeLinejoin="round" fill="none"
  dangerouslySetInnerHTML=${{ __html: P[d] || "" }}/>`;

/* ── spring-counted integer ──────────────────────────────────────────────── */
export function Ticker({ value, decimals = 0 }){
  const mv = useMotionValue(value);
  const sp = useSpring(mv, { stiffness:130, damping:24 });
  const [t,setT] = useState(String(value));
  useEffect(()=>{ mv.set(value); },[value,mv]);
  useEffect(()=> sp.on("change", v=> setT(decimals ? v.toFixed(decimals) : String(Math.round(v)))),[sp,decimals]);
  return html`<b>${t}</b>`;
}

/* ── streaming text: the AI writes, it does not appear ───────────────────── */
export function Stream({ text, speed = 13, start = 0, onDone, hi = [] }){
  const [n,setN] = useState(0);
  useEffect(()=>{
    setN(0);
    let i = 0, timer = 0, kick = setTimeout(tick, start);
    function tick(){
      i += Math.random() < .22 ? 2 : 1;
      setN(Math.min(i, text.length));
      if(i < text.length) timer = setTimeout(tick, speed + Math.random()*speed);
      else onDone && onDone();
    }
    return ()=>{ clearTimeout(kick); clearTimeout(timer); };
  },[text,speed,start]);
  const shown = text.slice(0,n);
  const done = n >= text.length;
  const parts = [];
  if(hi.length){
    let rest = shown, key = 0;
    const rx = new RegExp("(" + hi.map(escapeRx).join("|") + ")","g");
    let last = 0, m;
    while((m = rx.exec(shown))){
      if(m.index > last) parts.push(shown.slice(last,m.index));
      parts.push(html`<span key=${"h"+(key++)} className="tok">${m[0]}</span>`);
      last = m.index + m[0].length;
    }
    if(last < shown.length) parts.push(shown.slice(last));
    void rest;
  } else parts.push(shown);
  return html`<${F}>${parts}${!done && html`<span className="cursor"/>`}<//>`;
}
const escapeRx = s => s.replace(/[.*+?^${}()|[\]\\]/g,"\\$&");

/**
 * Soft≠Live: never paint raw backend paths or HTTP method envelopes into chrome.
 * Operators see product language only; wire routes stay in contract / network only.
 */
export function softChromeMeta(raw) {
  const s = String(raw == null ? "" : raw).trim();
  if (!s) return "";
  /* Method + path bags, path-only crumbs, residual "…/foo" desks. */
  if (
    /^\s*(GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS)\b/i.test(s) ||
    /(?:^|[\s·|])\/[A-Za-z0-9_{}\-./?=&%]+/.test(s) ||
    /\b\/api\//i.test(s) ||
    /\bPage\d+\b/.test(s) ||
    /\bplane\b/i.test(s)
  ) {
    if (/posture|status/i.test(s)) return "Live status";
    if (/list|register|catalog|roster/i.test(s)) return "Live list";
    if (/policy/i.test(s)) return "Policy";
    if (/practice|dry.?run/i.test(s)) return "Practice";
    if (/export|download|backup/i.test(s)) return "Export";
    if (/dispatch|command|act/i.test(s)) return "Live action";
    if (/soft/i.test(s) && /forbid|invent/i.test(s)) return "Unavailable";
    return "Live";
  }
  return s;
}

/**
 * FOUND-1 §3.7.10: strip bare trailing "· Live" / Page crumbs from operator verbs.
 * Trust chips stay separate; buttons use View / Configure / Run / Stop / Retry.
 */
export function productCopyOf(raw) {
  let s = String(raw == null ? "" : raw).trim();
  if (!s) return "";
  s = s.replace(/\bPage\d+\b/gi, "").replace(/\s{2,}/g, " ").trim();
  s = softChromeMeta(s);
  s = s.replace(/\s*[·•]\s*Live\s*$/i, "").trim();
  s = s.replace(/\s+Live\s*$/i, "").trim();
  return s || "Ready";
}

/* ── panel atoms ─────────────────────────────────────────────────────────── */
export const Sec = ({ title, meta, children, cls, className, ...rest }) => {
  const m = softChromeMeta(meta);
  return html`
  <section className=${"sec " + (cls || className || "")} ...${rest}>
    ${title && html`<div className="sec__h"><h3>${title}</h3>${m && html`<em>${m}</em>`}</div>`}
    ${children}
  </section>`;
};

/** Soft≠Live: never paint nested Live objects as React children (React #31). */
export function cmcPlainOf(v) {
  if (v == null) return "—";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return String(v);
  if (typeof v === "object") {
    const g = v.gap != null ? v.gap
      : (v.message != null ? v.message
        : (v.status != null && (typeof v.status === "string" || typeof v.status === "number" || typeof v.status === "boolean")
          ? v.status
          : (v.detail != null && (typeof v.detail === "string" || typeof v.detail === "number") ? v.detail : null)));
    if (g != null && (typeof g === "string" || typeof g === "number" || typeof g === "boolean")) return String(g);
    try {
      return JSON.stringify(v);
    } catch {
      return "—";
    }
  }
  return String(v);
}

export const KV = ({ k, v, tone }) =>
  html`<div className="kv"><span>${k}</span><b className=${tone === undefined ? "hi" : tone}>${cmcPlainOf(v)}</b></div>`;

export const Card = ({ title, sub, meta, ai, pad, children, cls }) => {
  const m = softChromeMeta(meta);
  return html`
  <${M.section} className=${"card glass "+(ai?"glass--ai ":"")+(cls||"")}
      style=${pad ? { padding:pad } : null}
      initial=${{ opacity:0, y:14 }} animate=${{ opacity:1, y:0 }} transition=${SOFT}>
    <div className="card__in">
      ${(title||m) && html`
        <div className="sec__h" style=${{ marginBottom: sub ? 4 : 16 }}>
          <h2 style=${{ margin:0, fontSize:"var(--t-read)", fontWeight:600 }}>${title}</h2>
          ${m && html`<em style=${{ fontStyle:"normal", fontFamily:"var(--mono)", fontSize:"var(--t-micro)", color:"var(--fg-3)" }}>${m}</em>`}
        </div>`}
      ${sub && html`<p className="sub">${sub}</p>`}
      ${children}
    </div>
  <//>`;
};

export const Field = ({ label, hint, bad, children }) => html`
  <label className="field">
    ${label && html`<u>${label}</u>`}
    ${children}
    ${hint && html`<p className="hint" data-bad=${bad?"1":"0"}>${hint}</p>`}
  </label>`;

export function Input({ icon, act, onAct, bad, ...rest }){
  return html`<div className="inp" data-bad=${bad?"1":"0"}>
    ${icon && html`<${Ico} d=${icon} w=${15}/>`}
    <input ...${rest}/>
    ${act && html`<button type="button" className="inp__act" onClick=${onAct}>${act}</button>`}
  </div>`;
}

/**
 * Craft select · closed surface matches bare .inp chrome; open list is on-theme.
 * Native <select> options cannot follow desk CSS — use this when the list is visible craft.
 * onChange receives a select-like event: { target: { value } }.
 */
export function CraftSelect({
  value = "",
  onChange,
  options = [],
  placeholder = "Select…",
  allowEmpty = false,
  disabled = false,
  bad = false,
  className = "",
  drop = "auto",
  id,
  "aria-label": ariaLabel,
}) {
  const [open, setOpen] = useState(false);
  const [dropDir, setDropDir] = useState(drop === "up" ? "up" : "down");
  const root = useRef(null);
  /** After pick, menu unmounts under the pointer — block the ghost click that would re-open. */
  const ignoreToggleUntil = useRef(0);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (root.current && !root.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const t = setTimeout(() => {
      addEventListener("pointerdown", onDoc, true);
      addEventListener("keydown", onKey);
    }, 0);
    return () => {
      clearTimeout(t);
      removeEventListener("pointerdown", onDoc, true);
      removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (drop === "up" || drop === "down") {
      setDropDir(drop);
      return undefined;
    }
    if (!open || !root.current) return undefined;
    const place = () => {
      const el = root.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const menu = el.querySelector(".craft-sel__menu");
      const need = Math.max(160, menu ? menu.getBoundingClientRect().height : 220) + 12;
      const below = window.innerHeight - r.bottom;
      const above = r.top;
      setDropDir(below < need && above > below ? "up" : "down");
    };
    place();
    const t = requestAnimationFrame(place);
    return () => cancelAnimationFrame(t);
  }, [open, drop, options && options.length]);

  const items = options.map((o) =>
    typeof o === "string"
      ? { value: o, label: o, hint: "" }
      : {
          value: o.value != null ? o.value : o.id,
          label: o.label != null ? o.label : String(o.value != null ? o.value : o.id),
          hint: o.hint != null ? String(o.hint) : "",
        },
  );
  const cur = items.find((x) => String(x.value) === String(value));
  const empty = value === "" || value == null || !cur;
  const shown = empty ? placeholder : cur.label;

  const pick = (v) => {
    ignoreToggleUntil.current = Date.now() + 450;
    setOpen(false);
    if (onChange) onChange({ target: { value: v } });
  };

  const onOptPointerDown = (e, v) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    pick(v);
  };

  return html`<div
      ref=${root}
      className=${"craft-sel " + (className || "")}
      data-open=${open ? "1" : "0"}
      data-disabled=${disabled ? "1" : "0"}
      data-bad=${bad ? "1" : "0"}
      data-drop=${dropDir === "up" ? "up" : "down"}>
    <button
      type="button"
      id=${id}
      className="craft-sel__trig"
      disabled=${disabled}
      aria-haspopup="listbox"
      aria-expanded=${open ? "true" : "false"}
      aria-label=${ariaLabel}
      data-empty=${empty ? "1" : "0"}
      onClick=${(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (disabled) return;
        if (Date.now() < ignoreToggleUntil.current) return;
        setOpen((o) => !o);
      }}>
      <span className="craft-sel__val">${shown}</span>
      <span className="craft-sel__caret" aria-hidden="true"/>
    </button>
    ${open && !disabled
      ? html`<ul className="craft-sel__menu" role="listbox"
          onPointerDown=${(e) => e.stopPropagation()}>
          ${allowEmpty
            ? html`<li>
                <button type="button" role="option" className="craft-sel__opt"
                  data-on=${empty ? "1" : "0"}
                  aria-selected=${empty}
                  onPointerDown=${(e) => onOptPointerDown(e, "")}>
                  <span className="craft-sel__opt-main">
                    <span>${placeholder}</span>
                  </span>
                </button>
              </li>`
            : null}
          ${items.map((it) => html`<li key=${"o-" + String(it.value)}>
            <button type="button" role="option" className="craft-sel__opt"
              data-on=${String(it.value) === String(value) ? "1" : "0"}
              data-hint=${it.hint ? "1" : "0"}
              aria-selected=${String(it.value) === String(value)}
              onPointerDown=${(e) => onOptPointerDown(e, it.value)}>
              <span className="craft-sel__opt-main">
                <span>${it.label}</span>
                ${it.hint ? html`<em className="craft-sel__hint">${it.hint}</em>` : null}
              </span>
              ${String(it.value) === String(value)
                ? html`<${Ico} d="check" w=${12}/>`
                : null}
            </button>
          </li>`)}
        </ul>`
      : null}
  </div>`;
}

export const Toggle = ({ on, onChange, title, say }) => html`
  <button className="opt" data-on=${on?"1":"0"} onClick=${()=>onChange(!on)} type="button">
    <span className="txt"><b>${title}</b>${say && html`<span>${say}</span>`}</span>
    <span className="sw"><i/></span>
  </button>`;

export const Alert = ({ tone, icon, title, say }) => html`
  <${M.div} className="alert" data-tone=${tone||"bad"}
      initial=${{ opacity:0, y:-6 }} animate=${{ opacity:1, y:0 }} transition=${FADE}>
    <${Ico} d=${icon || (tone==="info" ? "info" : "alert")} w=${15}/>
    <span className="txt"><b>${title}</b>${say && html`<span>${say}</span>`}</span>
  <//>`;

export const Steps = ({ at, items }) => html`
  <div className="steps">
    ${items.map((s,i)=> html`<${F} key=${s}>
      ${i>0 && html`<span className="steps__l"/>`}
      <span className="steps__s" data-on=${i===at?"1":"0"} data-done=${i<at?"1":"0"}>
        <u>${i<at ? html`<${Ico} d="check" w=${10} s=${{ strokeWidth:2.6 }}/>` : i+1}</u><b>${s}</b>
      </span>
    <//>`)}
  </div>`;

/* ── toasts ──────────────────────────────────────────────────────────────── */
export function useToasts(){
  const [list,setList] = useState([]);
  const push = useCallback((msg,tone)=>{
    const key = Math.random().toString(36).slice(2);
    setList(t=>[...t,{ key,msg,tone }]);
    setTimeout(()=>setList(t=>t.filter(x=>x.key!==key)), 3200);
  },[]);
  return [list, push];
}

/* ── ticking wall clock · real instant projected in the user's IANA zone ── */
export function useClock(me){
  const [t,setT] = useState("");
  useEffect(()=>{
    const fmt = () => {
      setT(formatInstant(new Date(), {
        timeZone: timezoneOf(me),
        date: false,
        zoneName: false,
      }));
    };
    fmt();
    const id = setInterval(fmt, 1000);
    addEventListener(TIMEZONE_CHANGED_EVENT, fmt);
    return ()=>{
      clearInterval(id);
      removeEventListener(TIMEZONE_CHANGED_EVENT, fmt);
    };
  },[me && me.username, me && me.timezone]);
  return t;
}

/* ── hash router ─────────────────────────────────────────────────────────── */
/** Pathname only. Query after `#/path?k=` is context, never a subroute. */
export function parseHashRoute(raw){
  const s = String(raw == null ? "" : raw).trim() || "/";
  const q = s.indexOf("?");
  const pathname = (q >= 0 ? s.slice(0, q) : s) || "/";
  const search = q >= 0 ? s.slice(q + 1) : "";
  return { pathname, search, searchParams: new URLSearchParams(search) };
}

/** Known handoff keys only — unknown query is never treated as an id. */
export const HANDOFF_QUERY_KEYS = Object.freeze([
  "device", "incident", "type", "view", "owner", "return_to", "from", "handoff", "zone",
  "lane",
]);

/** Parse query from the revealed inner path so `#/cx/TOKEN?lane=mcp` survives refresh. */
export function hashSearchParamsOf(rawHash) {
  const raw = String(rawHash == null ? "" : rawHash).replace(/^#/, "");
  let inner = raw;
  try { inner = reveal(raw) || raw; } catch { inner = raw; }
  return parseHashRoute(inner).searchParams;
}

export function hashSearchParams(){
  return hashSearchParamsOf(typeof location !== "undefined" ? location.hash : "");
}

export function handoffKnownParamsOf(sp){
  const out = {};
  if (!sp || typeof sp.get !== "function") return out;
  for (const k of HANDOFF_QUERY_KEYS) {
    const v = String(sp.get(k) || "").trim();
    if (v) out[k] = v;
  }
  return out;
}

/** Mirror helpers._sibling_group_norm: strip → NFC → collapse whitespace → casefold. */
export function canonicalGroupKey(v){
  let s = String(v == null ? "" : v).trim();
  if (!s) return "";
  try { s = s.normalize("NFC"); } catch (_) { /* keep s */ }
  s = s.split(/\s+/).join(" ");
  try { return s.toLocaleLowerCase(); } catch (_) { return s.toLowerCase(); }
}

export function sameGroupKey(a, b){
  return canonicalGroupKey(a) === canonicalGroupKey(b);
}

export function useHash(fallback = "/ops"){
  const [h,setH] = useState(()=> (location.hash || "#"+fallback).slice(1));
  useEffect(()=>{
    const on = ()=> setH((location.hash || "#"+fallback).slice(1));
    addEventListener("hashchange",on); return ()=>removeEventListener("hashchange",on);
  },[fallback]);
  return [h, p=>{ location.hash = resolveCanon(p); }];
}

/** Soft≠Live: no Live write path — never toast Soft draft success. */
export const refuseLive = (toast, what) =>
  toast((what || "This control") + " has no Live write path yet — nothing was sent", "warn");

/** Product SoT: plaintext hash after alias fixed-point. Opaque CX is not nav SoT. */
export const go = p => { location.hash = resolveCanon(p); };

/**
 * Keep a workspace mounted while its local instrument changes.
 * The URL remains copyable, but no hashchange is emitted, so drafts, scroll,
 * selected evidence, and open drawers are not torn down between sibling views.
 */
export const stay = p => {
  const next = resolveCanon(p);
  const href = String(location.pathname || "") + String(location.search || "") + "#" + next;
  history.replaceState(history.state, "", href);
  return next;
};

/* ── close on outside click ──────────────────────────────────────────────── */
export function useDismiss(open, close){
  useEffect(()=>{
    if(!open) return;
    const on = ()=> close();
    const id = setTimeout(()=> addEventListener("click",on), 0);
    return ()=>{ clearTimeout(id); removeEventListener("click",on); };
  },[open,close]);
}

export function useEsc(fn){
  useEffect(()=>{
    const on = e => { if(e.key === "Escape") fn(); };
    addEventListener("keydown",on); return ()=>removeEventListener("keydown",on);
  },[fn]);
}

let overlayLockCount = 0;
let overlayBodyOverflow = "";

/**
 * Shared overlay discipline: lock document scroll while open and restore focus
 * to the element that launched the overlay when it closes. This is deliberately
 * not a full focus trap; dialogs must still provide aria-modal and an initial
 * focus target.
 */
export function useOverlayLock(open){
  const restoreFocus = useRef(null);
  useEffect(()=>{
    if(!open || typeof document === "undefined") return;
    restoreFocus.current = document.activeElement;
    if(overlayLockCount === 0){
      overlayBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }
    overlayLockCount += 1;
    return ()=>{
      overlayLockCount = Math.max(0, overlayLockCount - 1);
      if(overlayLockCount === 0) document.body.style.overflow = overlayBodyOverflow;
      const target = restoreFocus.current;
      if(target && target.isConnected && typeof target.focus === "function"){
        requestAnimationFrame(()=>target.focus({ preventScroll:true }));
      }
    };
  },[open]);
}

export const stop = e => e.stopPropagation();
export const pct = n => Math.round(n*100)+"%";
